import axios from "axios";
import HttpService from "./HttpService";

const MemberService = {
    loadMemberships: (userId, cancelToken) => {
        return HttpService.get(
            `/api/portal/users/${userId}/memberships`, 
            { 
                cancelToken
            })
            .then(response => response.data)
    },
  loadMembershipMaf: (userId, cancelToken) => {
    return HttpService.get(`/api/portal/users/${userId}/memberships-maf`, {
      cancelToken,
    })
      .then((response) => response.data)
      .then((data) => {
        // The backend returns one item per active membership (or, only when there are no
        // active memberships, one per pending NME) so an individual with more than one
        // gets a separate MAF entry for each instead of just the first.
        const items = Array.isArray(data) ? data : [];

        return items.map((item) => {
          const membershipMaf = item?.membershipMaf || null;
          const membership = item?.membership
            ? {
                ...item.membership,
                membershipMaf,
              }
            : null;

          return {
            membership,
            nme: item?.nme || null,
            membershipMaf,
            localUnion: item?.localUnion || null,
            individual: item?.individual || null,
          };
        });
      });
  },
  getMemberACHStatus: (userId, cancelToken) => {
    return HttpService.get(`/api/portal/users/${userId}/ach`, {
      cancelToken,
    }).then((response) => response.data);
  },
  createMemberACH: (userId, cancelToken) => {
    return HttpService.post(`/api/portal/users/${userId}/ach`, null, {
      cancelToken,
    })
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error creating Member ACH:", error);

        // Check if the error response has a 'cause' property and use it
        let errorMessage =
          "An error occurred while setting up your payment method.";

        if (error?.response?.data?.cause) {
          errorMessage = error.response.data.cause;
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        // Create a new error with the custom message but preserve the original error
        const customError = new Error(errorMessage);
        customError.originalError = error;
        customError.response = error.response;

        throw customError;
      });
  },
  checkIfEmailIsInUse: (
    email,
    dateOfBirth,
    firstName,
    lastName,
    cancelToken,
  ) => {
    return HttpService.post(
      "/api/portal/members/find",
      {
        member: {
          email,
          dateOfBirth,
          firstName,
          lastName,
        },
      },
      {
        cancelToken,
      },
    ).then((response) => response.data);
  },
  checkPrimaryEmailIntegrity: (guid, email, cancelToken) => {
    return HttpService.post(
      `/api/portal/registration/${guid}/checkEmail`,
      {
        email,
      },
      {
        cancelToken,
      },
    ).then((response) => response.data);
  },
  getMemberBenefitsAccess: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/memberbenefitsaccess`, {
      cancelToken,
    }).then((response) => response.data);
  },
  getMembershipBenefitsDepartmentPhone: (cancelToken) => {
    return HttpService.get(
      `/api/portal/settings/membershipbenefitsdepartmentphone`,
      {
        cancelToken,
      },
    ).then((response) => response.data);
  },
  getMemberId: (userId, cancelToken) => {
    return HttpService.get(`/api/portal/members/${userId}/memberId`, {
      cancelToken,
    }).then((response) => response.data);
  },
  getMostRecentNewMemberEnrollmentInfo: (userId, cancelToken) => {
    return HttpService.post(
      "/api/system/entities/filters/15122",
      {
        parameters: [
          {
            name: "EntityID",
            value: userId,
            dataType: "int",
            direction: "in",
          },
          {
            name: "EntityTypeID",
            value: 3,
            dataType: "int",
            direction: "in",
          },
        ],
      },
      {
        cancelToken,
      },
    )
      .then((response) => response.data)
      .then((result) => result[0])
      .then(({ EntityID, EntityTypeID, PaymentAgreementText }) => ({
        entityId: EntityID,
        entityTypeId: EntityTypeID,
        paymentAgreementText: PaymentAgreementText,
      }));
  },
  sendRegistrationEmailIfMemberExists: (
    firstName,
    lastName,
    dateOfBirth,
    cancelToken,
  ) => {
    return HttpService.post(
      "/api/portal/registration",
      {
        user: {
          firstName,
          lastName,
          dateOfBirth,
        },
      },
      {
        cancelToken,
      },
    ).then((response) => response.data);
  },
  getOrganizationName: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/organizationname`, {
      cancelToken,
    }).then((response) => response.data);
  },
  getPortalTitle: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/portaltitle`, {
      cancelToken,
    })
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error fetching portal title:", error);
        return "Membership Portal"; // Default fallback
      });
  },
  getPortalTitleColor: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/portaltitlecolor`, {
      cancelToken,
    })
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error fetching portal title color:", error);
        return "#c3c3c3"; // Default fallback to gray
      });
  },
  getTermsOfService: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/termsofservice`, {
      cancelToken,
    }).then((response) => response.data);
  },
  getMemberAuthorizationFormHeader: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/memberauthorizationformheader`, {
      cancelToken,
    }).then((response) => response.data);
  },
  getMemberAuthorizationFloridaNotice: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/memberauthorizationfloridanotice`, {
      cancelToken,
    }).then((response) => response.data);
  },
  getMemberAuthorizationFormInstructions: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/memberauthorizationforminstructions`, {
      cancelToken,
    }).then((response) => response.data);
  },
  getTermsOfServiceLink: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/termsofservicelink`, {
      cancelToken,
    }).then((response) => response.data);
  },
  getFavicon: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/favicon`, {
      cancelToken,
    }).then((response) => response.data);
  },
  getBackgroundImage: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/backgroundimage`, {
      cancelToken,
    }).then((response) => response.data);
  },
  getPortalLogoNoBackground: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/portallogonobackground`, {
      cancelToken,
    }).then((response) => response.data);
  },
  getPortalLogoWithBackground: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/portallogowithbackground`, {
      cancelToken,
    }).then((response) => response.data);
  },
  getPrivacyPolicy: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/privacypolicy`, {
      cancelToken,
    }).then((response) => response.data);
  },
  getPrivacyPolicyLink: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/privacypolicylink`, {
      cancelToken,
    }).then((response) => response.data);
  },
  getOrganizationShortName: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/organizationshortname`, {
      cancelToken,
    })
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error fetching organization short name:", error);
        return ""; // Default fallback
      });
  },
  getOrganizationEmail: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/organizationemail`, {
      cancelToken,
    })
      .then((response) => response.data)
      .catch((error) => {
        if (axios.isCancel(error)) throw error;
        console.error("Error fetching organization email:", error);
        return "(email not set)"; // Default fallback
      });
  },
  getConsentExpirationNotes: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/consentexpirationnotes`, {
      cancelToken,
    })
      .then((response) => response.data)
      .catch((error) => {
        if (axios.isCancel(error)) throw error;
        console.error("Error fetching consent expiration notes:", error);
        return ""; // Default fallback
      });
  },
  getDirectoryWebsite: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/directorywebsite`, {
      cancelToken,
    })
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error fetching directory website:", error);
        return "https://example.org/directory"; // Default fallback
      });
  },
  getTextMessageConsent: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/textmessageconsent`, {
      cancelToken,
    })
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error fetching text message consent:", error);
        return "By providing my phone number, I understand that my local association, the Organization, the National Education Association, and the American Federation of Teachers may use automated calling techniques and/or text message me on a regular basis. These entities will never charge me for text message alerts. Carrier message and data rates may apply to such alerts."; // Default fallback
      });
  },
  getJobTitleExamples: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/jobtitleexamples`, {
      cancelToken,
    })
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error fetching job title examples:", error);
        return "Math Teacher, Teacher 3rd, Bus Driver, Custodian, Graduate Assistant, Professor Physics"; // Default fallback
      });
  },
  getPortalDividerColor: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/portaldividercolor`, {
      cancelToken,
    })
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error fetching portal divider color:", error);
        return "#c3c3c3"; // Default fallback
      });
  },
  getPortalPanelColor: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/portalpanelcolor`, {
      cancelToken,
    })
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error fetching portal panel color:", error);
        return "#c3c3c3"; // Default fallback
      });
  },
  getSmartyStreetsEmbeddedToken: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/smartystreetsembeddedtoken`, {
      cancelToken,
    })
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error fetching SmartyStreets embedded token:", error);
        return ""; // Default fallback
      });
  },
  getSmartyStreetsApiUrl: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/smartystreetsapiurl`, {
      cancelToken,
    })
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error fetching SmartyStreets API URL:", error);
        return "https://us-autocomplete-pro.api.smarty.com"; // Default fallback
      });
  },
  getGoogleRecaptchaV2Key: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/googlerecaptchav2key`, {
      cancelToken,
    })
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error fetching Google reCAPTCHA V2 key:", error);
        return ""; // Default fallback
      });
  },
  getUseRecaptcha: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/userecaptcha`, {
      cancelToken,
    })
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error fetching Use ReCAPTCHA setting:", error);
        return true; // Default fallback to true (require reCAPTCHA by default)
      });
  },
  getEnableMafV2: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/enablemafv2`, {
      cancelToken,
    })
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error fetching Enable MAFv2 setting:", error);
        return false; // Default fallback (show V1)
      });
  },
  getMaxSalary: (cancelToken) => {
    return HttpService.get(`/api/portal/settings/maxsalary`, {
      cancelToken,
    })
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error fetching max salary setting:", error);
        return 0; // Default fallback
      });
  },
  submitMemberAuthorization: (memberAuthInfo, cancelToken) => {
    return HttpService.post(`/api/portal/MemberAuthorization`, memberAuthInfo, {
      cancelToken,
    }).then((response) => response.data);
  },
  downloadAttachment: (attachmentId, cancelToken) => {
    return HttpService.get(`/api/attachments/${attachmentId}`, {
      cancelToken,
    }).then((response) => response.data);
  },
};

export default MemberService;